import { Component, OnInit, forwardRef, Input, Output, EventEmitter, ElementRef, viewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, FormControl } from '@angular/forms';
import { Observable, Subject, of, concat } from 'rxjs';
import { tap, switchMap, catchError, map, debounceTime } from 'rxjs/operators';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-outlet-properties',
  templateUrl: './outlet-properties.component.html',
  styleUrls: ['./outlet-properties.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => OutletPropertiesComponent),
    multi: true
  }],
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    CommonModule
  ]
})
export class OutletPropertiesComponent implements OnInit, ControlValueAccessor {

  addOnBlur = false;
  separatorKeysCodes: number[] = [13, 188];
  labelCtrl = new FormControl();
  filteredLabels: Observable<string[]>;

  @Input() readOnly: false;
  @Input() placeholder: string;
  @Input() labels: string[] = [];
  @Input() readonlyLabels: string[] = [];

  @Output() changed = new EventEmitter<string[]>();

  readonly labelInput = viewChild<ElementRef<HTMLInputElement>>('labelInput');
  readonly matAutocomplete = viewChild<MatAutocomplete>('auto');
  readonly matAutocompleteTrigger = viewChild<MatAutocompleteTrigger>('trigger');

  refreshAutocomplete = new Subject<string>(); // used by parent to notify when the autocomplete datasource has changed

  constructor() { }

  ngOnInit() {
    this.filteredLabels = this.refreshAutocomplete
      .pipe(
        tap(() => {}),
        switchMap(() =>
          concat(of(this.labelCtrl.value), this.labelCtrl.valueChanges).pipe(debounceTime(100))),
        tap(() => {}),
        switchMap(input => this.getTagsQuery(input || '').pipe(catchError(() => of([]))))
      );
  }

  private tagKeys: string[] = ['AdministrativeAreaL1', 'AdministrativeAreaL2'];
  private tagValues: string[] = [];

  private getTagsQuery(input: string): Observable<string[]> {
    const containsSeparator = input.indexOf(':') >= 0;
    let [tag, val] = input.split(':');
    let obsTags = of(<string[]>[]);
    // if (!this.tagKeys)
    //   obsTags =  this.api.getTagSuggestions('').pipe(tap(res => this.tagKeys = res));
    // else
    obsTags = of(this.tagKeys);
    if (!containsSeparator) {
      return obsTags.pipe(map(t => t.filter(v => v.toLocaleLowerCase().indexOf(tag.toLocaleLowerCase()) >= 0)));
    }
    if (!val) val = '';
    let obsValues = of(<string[]>[]);
    // if (this.lastTagKey !== tag || !this.tagValues) {
    //   this.lastTagKey = tag;
    //   obsValues = this.api.getTagValuesSuggestions(tag, '').pipe(tap(tv => this.tagValues = tv));
    // } else {
    //   obsValues = of(this.tagValues);
    // }
    obsValues = of(this.tagValues);
    return obsValues.pipe(
      map(t => t
        .filter(v => v.toLocaleLowerCase().indexOf(val.toLocaleLowerCase()) >= 0)
        .map(v => `${tag}:${v}`)
      )
    );
  }

  onOpened(_e: any) {
    this.forceRefreshAutocomplete();
  }

  forceRefreshAutocomplete() {
    // this.labelCtrl.setValue(null);
    this.refreshAutocomplete.next(null);
  }

  add(event: MatChipInputEvent): void {
    if (this.readOnly)
      return;

    // Add label only when MatAutocomplete is not open to make sure this does not conflict with OptionSelected Event
    if (this.matAutocomplete().isOpen)
      return;

    if (!this.labels)
      this.labels = [];

    const input = event.input;
    const value = event.value;

    // Add our label
    const label = (value || '').trim();
    if (label.indexOf(':') < 0 || !label.split(':')[1])
      return;

    if (label) {
      const tv = label.split(':');
      const isValid = tv.length === 2 && tv[0] && tv[1];
      const alreadyAdded = this.labels.filter(x => x.trim().toLowerCase() === label.toLocaleLowerCase()).length > 0;
      if (!alreadyAdded && isValid) {
        this.labels.push(value.trim());
        this.changed.emit(this.labels);
      }
    }

    // Reset the input value
    if (input)
      input.value = '';

    this.labelCtrl.setValue(null);
    this.labelInput().nativeElement.blur();
    this.matAutocompleteTrigger().closePanel();
  }

  remove(label: string): void {
    if (this.readOnly)
      return;

    const index = this.labels.indexOf(label);
    if (index >= 0) {
      this.labels.splice(index, 1);
      this.changed.emit(this.labels);
      this.propagateChange(this.labels);
    }
    this.labelInput().nativeElement.blur();
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    if (this.readOnly)
      return;

    if (!this.labels)
      this.labels = [];

    const label = event.option.viewValue;
    if (label.indexOf(':') < 0) {
      this.labelInput().nativeElement.value = label + ':';
      this.labelCtrl.setValue(label + ':');
      this.labelInput().nativeElement.focus();
      setTimeout(() => {
        this.matAutocompleteTrigger().openPanel();
      }, 1);
      return;
    }
    const alreadyAdded = this.labels.filter(x => x.trim().toLowerCase() === label.toLocaleLowerCase()).length > 0;
    // this.labelInput.nativeElement.value = label;
    // return;
    if (!alreadyAdded) {
      this.labels.push(label);
      this.changed.emit(this.labels);
      this.propagateChange(this.labels);
    }

    this.labelInput().nativeElement.value = '';
    this.labelInput().nativeElement.blur();
    this.labelCtrl.setValue(null);
  }


  // ControlValueAccessor
  propagateChange = (_: any) => { };
  writeValue(obj: any): void {
    if (!obj) {
      this.labels = [];
    } else if (typeof obj === 'string') {
      this.labels = [obj];
    } else if (obj.length) {
      this.labels = obj;
    } else {
      this.labels = [];
    }
  }
  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }
  registerOnTouched(_fn: any): void {
  }


  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    event.clipboardData
      .getData('Text')                       // gets the text pasted
      .split(/;|,|\n/)                     // splits it when a SEMICOLON or COMMA or NEWLINE or SPACE
      .forEach(value => {
        if (value.trim()) {                 // push if valid
          this.labels.push(value.trim());
          this.changed.emit(this.labels);
          this.propagateChange(this.labels);
        }
      });
  }
}
